import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, CheckCircle2, Loader2, Mail, MapPin, Phone } from 'lucide-react';
import { submitContactInquiry, type ContactInquiryData } from '../services/contactService';

export function LakeCharlesContactPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [formData, setFormData] = useState<ContactInquiryData>({
    fullName: '',
    email: '',
    subject: '',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear errors when user starts typing
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const result = await submitContactInquiry(formData);

      if (result.success) {
        setSubmitted(true);
        setFormData({ fullName: '', email: '', subject: '', message: '' });
        // Auto-hide success message after 8 seconds
        setTimeout(() => setSubmitted(false), 8000);
      } else {
        setError(result.error || 'Failed to submit contact inquiry. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="bg-primary/5 py-12 sm:py-16 md:py-20">
        <div className="container max-w-4xl px-4 sm:px-6">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-4">
            Contact Us
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl">
            Have questions about the Lake Charles Community Platform? Need to exercise your privacy
            rights or request data deletion? We're here to help. Send us a message and we'll respond
            within 5-7 business days.
          </p>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-12 sm:py-16">
        <div className="container max-w-4xl px-4 sm:px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {/* Contact Methods */}
            <div className="md:col-span-1 space-y-6">
              <div>
                <h2 className="text-xl font-semibold mb-4">Get In Touch</h2>
                <p className="text-sm text-muted-foreground mb-6">
                  We value your privacy and are committed to protecting your personal data.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Mail className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Email</p>
                    <p className="text-sm text-muted-foreground">Use the form to contact us</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Location</p>
                    <p className="text-sm text-muted-foreground">Lake Charles, Louisiana</p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Phone className="h-5 w-5 text-primary mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">Response Time</p>
                    <p className="text-sm text-muted-foreground">5-7 business days</p>
                  </div>
                </div>
              </div>

              <div className="bg-muted/50 p-4 rounded-lg mt-6">
                <p className="text-xs text-muted-foreground">
                  <strong>Privacy Notice:</strong> Submissions are encrypted and stored securely.
                  Your data will only be used to respond to your inquiry. See our{' '}
                  <a href="/privacy" className="underline hover:text-primary">
                    Privacy Policy
                  </a>{' '}
                  for details.
                </p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="md:col-span-2">
              <Card>
                <CardHeader>
                  <CardTitle>Send Us a Message</CardTitle>
                  <CardDescription>
                    Fill out the form below and we'll get back to you as soon as possible
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {error && (
                    <Alert variant="destructive" className="mb-6">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  {submitted && (
                    <Alert className="mb-6 border-green-200 bg-green-50 dark:bg-green-950 dark:border-green-800">
                      <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                      <AlertDescription className="text-green-800 dark:text-green-200">
                        <strong>Thank you for your message!</strong> We've received your inquiry and
                        will respond within 5-7 business days.
                      </AlertDescription>
                    </Alert>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="fullName">
                          Full Name <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="fullName"
                          name="fullName"
                          type="text"
                          placeholder="John Doe"
                          value={formData.fullName}
                          onChange={handleChange}
                          required
                          maxLength={120}
                          disabled={isSubmitting}
                          className="mt-1.5"
                        />
                      </div>

                      <div>
                        <Label htmlFor="email">
                          Email Address <span className="text-destructive">*</span>
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          placeholder="john@example.com"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          disabled={isSubmitting}
                          className="mt-1.5"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="subject">
                        Subject <span className="text-destructive">*</span>
                      </Label>
                      <Select
                        value={formData.subject}
                        onValueChange={(value) => {
                          setFormData((prev) => ({ ...prev, subject: value }));
                          if (error) setError(null);
                        }}
                        required
                        disabled={isSubmitting}
                      >
                        <SelectTrigger className="mt-1.5">
                          <SelectValue placeholder="Select inquiry type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Privacy Inquiry">Privacy Inquiry</SelectItem>
                          <SelectItem value="Data Deletion Request">
                            Data Deletion Request
                          </SelectItem>
                          <SelectItem value="Data Access Request">Data Access Request</SelectItem>
                          <SelectItem value="Data Correction Request">
                            Data Correction Request
                          </SelectItem>
                          <SelectItem value="Platform Information">Platform Information</SelectItem>
                          <SelectItem value="Technical Support">Technical Support</SelectItem>
                          <SelectItem value="Partnership Inquiry">Partnership Inquiry</SelectItem>
                          <SelectItem value="General Feedback">General Feedback</SelectItem>
                          <SelectItem value="Other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message">
                        Message <span className="text-destructive">*</span>
                      </Label>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="Please provide details about your inquiry. For data deletion requests, include your email address and any other identifying information."
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        maxLength={10000}
                        disabled={isSubmitting}
                        className="mt-1.5 resize-y"
                      />
                      <p className="text-xs text-muted-foreground mt-1.5">
                        {formData.message.length}/10,000 characters
                      </p>
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full sm:w-auto min-w-[200px]"
                      size="lg"
                    >
                      {isSubmitting ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Mail className="mr-2 h-4 w-4" />
                          Send Message
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-muted-foreground mt-4">
                      By submitting this form, you agree to our{' '}
                      <a href="/privacy" className="underline hover:text-primary">
                        Privacy Policy
                      </a>{' '}
                      and{' '}
                      <a href="/terms" className="underline hover:text-primary">
                        Terms of Service
                      </a>
                      .
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Common Inquiries */}
          <div className="bg-muted/30 rounded-lg p-6 sm:p-8">
            <h3 className="text-lg font-semibold mb-4">Common Inquiries</h3>
            <div className="grid sm:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="font-medium mb-1">Privacy & Data Deletion</p>
                <p className="text-muted-foreground">
                  Request to access, correct, or delete your personal data
                </p>
              </div>
              <div>
                <p className="font-medium mb-1">Platform Information</p>
                <p className="text-muted-foreground">
                  Questions about features, pricing, or community benefits
                </p>
              </div>
              <div>
                <p className="font-medium mb-1">Technical Support</p>
                <p className="text-muted-foreground">
                  Issues with account access, forms, or website functionality
                </p>
              </div>
              <div>
                <p className="font-medium mb-1">General Feedback</p>
                <p className="text-muted-foreground">
                  Suggestions, comments, or partnership inquiries
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
